<template>
  <div class="main-info">
    <div class="main-info_header">
      <base-skeleton v-if="loading" class="w-full h-[200px] rounded-md" />
      <div class="main-info_header_left">
        <div class="main-info_header_titles">
          <Text
            v-if="contractName && isContract"
            pattern="body3"
            :weight="600"
            :height="110"
            color="#A3A3A3"
          >
            {{ contractName }}
            <span v-if="contractSymbol">({{ contractSymbol }})</span>
          </Text>
          <Text
            v-else-if="subtitle"
            pattern="body3"
            :weight="600"
            :height="110"
            color="#A3A3A3"
          >
            {{ $t(subtitle) }}
          </Text>
        </div>
        <div v-if="isContract" class="main-info_header_contract">
          <BaseSvgIcon
            name="colored-check-green"
            class="main-info_header_contract_icon"
          />
          <Text pattern="body3" :weight="600" :height="100" color="#fff">
            {{ $t("Contract") }}
          </Text>
        </div>
      </div>
    </div>
    <div v-if="details?.length > 0" class="main-info_details">
      <div class="main-info__balance">
        <div class="main-info__balance-title">
          {{ t(details[0].label) }}
        </div>
        <div class="main-info__balance-value">
          {{ details[0].value }}
        </div>
        <div class="main-info__balance-usd">
          {{ getShortUsdAmount }}
        </div>
      </div>

      <div class="main-info__address">
        <div class="main-info__address-title">
          {{ t(details[1].label) }}
        </div>
        <div class="main-info__address-value">
          {{ details[1].value }}
        </div>
      </div>

      <div class="main-info_details_buttons">
        <BaseButton
          type="shade"
          outline
          class="main-info_details_button"
          @click="copyAddress(copyValue)"
        >
          <BaseSvgIcon name="copy" class="icon" />
          {{ $t("MainInfo | Copy") }}
        </BaseButton>
      </div>
    </div>
    <div v-else-if="!loading" class="main-info_details">
      <div class="main-info_details_empty">
        <BaseSvgIcon name="info-gray" class="icon" />
        <Text pattern="body3" :weight="600" :height="110" color="#A3A3A3">
          {{ $t("Address | Data failed to load") }}
        </Text>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";

// components
import { Text, BaseSvgIcon, BaseButton } from "@/shared/ui";
import BaseSkeleton from "@/shared/ui/BaseSkeleton/BaseSkeleton.vue";

// utils
import { copyToClipboard } from "@emcd/common/helpers";

// icons
import "@/assets/icons/SVG/bitcoin.svg";
import "@/assets/icons/SVG/tron.svg";
import "@/assets/icons/SVG/usdt.svg";
import "@/assets/icons/SVG/colored-check-green.svg";
import "@/assets/icons/SVG/pending.svg";
import "@/assets/icons/SVG/error2.svg";
import "@/assets/icons/SVG/success2.svg";
import "@/assets/icons/SVG/info-gray.svg";
import "@/assets/icons/SVG/copy.svg";
import "@/assets/icons/SVG/share.svg";

// props
const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  subtitle: {
    type: String,
    default: "",
  },
  isContract: {
    type: Boolean,
    default: false,
  },
  details: {
    type: Array,
    default: () => [],
  },
  copyValue: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: true,
  },
  contractName: {
    type: String,
    default: "",
  },
  contractSymbol: {
    type: String,
    default: "",
  },
  contractLogo: {
    type: String,
    default: "",
  },
});

const { t } = useI18n();
const { proxy } = getCurrentInstance();

// computed
const getShortUsdAmount = computed(() => {
  if (props.details[0].usdValue) {
    let usdValue = props.details[0].usdValue.replaceAll("$", "");
    return `$\u202F${Number(usdValue).toFixed(2)}`;
  }
  return "";
});

// methods
const copyAddress = (copyValue) => {
  copyToClipboard(copyValue, () => {});
  proxy.$toast({
    type: "success",
    title: t("AddressCopied"),
  });
  window.emcd.AnalyticsService.track("click", {
    event_type: "click",
    product: "explorer",
    screen: "address",
    button_name: "copy_address_hash",
  });
};
</script>

<style lang="scss" scoped>
.main-info {
  background: var(--Dark-900, #111);
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--Dark-850, #1a1a1a);
  overflow: hidden;

  &_header {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 16px;

    @media (width < 768px) {
      padding: 12px;
    }

    &_left {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      gap: 13px;
    }

    &_titles {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 4px;
      margin-right: 12px;
    }

    &_contract {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 6px 6.5px;
      gap: 6px;
      border-radius: 50px;
      background: var(--green-850);
      height: fit-content;

      &_icon {
        width: 23px;
        height: 23px;
      }
    }

    &_icon {
      position: absolute;
      top: 32px;
      right: 32px;
      width: 36px;
      height: 36px;
      pointer-events: none;

      @media (width < 768px) {
        top: 24px;
        right: 24px;
      }
    }
  }

  &_details {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0 32px 32px;

    @media (width < 768px) {
      padding: 0 24px 24px;
    }

    &_empty {
      display: flex;
      align-items: center;
      gap: 8px;

      .icon {
        width: 16px;
        height: 16px;
      }

      .text {
        line-height: 100% !important;
      }
    }

    &_buttons {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 8px;
    }

    &_button {
      width: 100%;
      justify-items: center;
      align-items: center;
      padding: 11px 2px;
      height: 36px;
      color: var(--Basic-White, #fff);
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 106%;
      gap: 10px;
      background: #1a1a1a;
      border: none;

      .icon {
        width: 16px;
        height: 16px;
        pointer-events: none;
      }

      &:hover {
        background: #ffffff;
        color: #000000;
        .icon {
          transition: all 0.35s ease;
          filter: brightness(0) saturate(100%);
        }
      }

      &:active {
        background: var(--light-500);
        color: #000000;
      }
    }

    &_item {
      &_label {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 3px;
      }

      &_value {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        gap: 8px;

        .annotation {
          white-space: nowrap;
        }
      }

      &_links {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        gap: 4px;
      }

      .underline {
        text-decoration: underline;
      }

      .status-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 4px;
      }

      .copy {
        min-width: 20px;
        min-height: 20px;
        transition: color 0.3s;
        cursor: pointer;
        color: #c18afd;

        &:hover {
          color: #fff;
        }
      }

      .contract-label {
        user-select: none;
        border-radius: 11px;
        border: 1px solid var(--Dark-700, #2d2d2d);
        padding: 3px 8px;
      }
    }
  }

  &__balance {
    &-title {
      margin-bottom: 4px;
      font-size: 14px;
      font-weight: 500;
      color: #7a7a7a;
    }

    &-value {
      font-size: 20px;
      font-weight: 600;
      color: #fafafa;
    }

    &-usd {
      margin-bottom: 20px;
      font-size: 14px;
      font-weight: 500;
      color: #7a7a7a;
    }
  }

  &__address {
    &-title {
      margin-bottom: 4px;
      font-size: 14px;
      font-weight: 500;
      color: #787878;
    }

    &-value {
      margin-bottom: 24px;
      font-size: 14px;
      font-weight: 500;
      color: #fff;
      word-break: break-word;
      overflow-wrap: break-word;
    }
  }
}
</style>
